import { useState } from 'react';

import { Alert, Button } from '@/components/ui';
import { retryCheckout } from '@/lib/api/checkout';
import { useApiMutation } from '@/lib/api/queryClient';
import type { OrderDetail } from '@/lib/api/types';

export interface PayAgainButtonProps {
  order: OrderDetail;
}

/**
 * The way back to paying, for an order that is not paid.
 *
 * WHY THIS HAD TO EXIST. The quote is consumed the moment the order is
 * placed, so the cart is empty from then on and checkout cannot be re-run.
 * Without this button a declined card was the end of the road: the order
 * page said "you can try again" and offered nothing to try it with, and the
 * customer's only option was to telephone the shop. Verified in a browser
 * before it was written, which is the only way it would have been found.
 *
 * It opens a NEW payment session rather than reusing the old link. The
 * original is created with an hour's life at the provider, so "go back to
 * payment" is a dead end for anyone who returns the next morning — and a
 * dead link is worse than no link, because it looks like the shop is broken
 * rather than like the session simply lapsed.
 *
 * Nothing is re-priced. The order is frozen, so the customer pays the total
 * they already agreed to, whatever the catalogue has done since.
 */
export function PayAgainButton({ order }: PayAgainButtonProps) {
  const [sending, setSending] = useState(false);

  const retry = useApiMutation<{ checkout_url: string }, void>({
    mutationFn: () => retryCheckout(order.id),
    onSuccess: (response) => {
      // A full navigation, not a router push: the gateway is somebody
      // else's site.
      setSending(true);
      window.location.assign(response.checkout_url);
    },
  });

  return (
    <>
      <p className="mt-2">
        <Button
          onClick={() => retry.mutate()}
          loading={retry.isPending || sending}
          loadingText="Taking you to payment…"
        >
          Pay again
        </Button>
      </p>

      {retry.error ? (
        <Alert severity="danger" title="We could not start another payment" live className="mt-2">
          <p>{retry.error.message}</p>
          <p className="mt-1">
            If this keeps happening, call the shop with your order number and we will take it from
            there.
          </p>
        </Alert>
      ) : null}
    </>
  );
}
